import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import { auth } from "../firebase";

const API =
  process.env.REACT_APP_SELLER_API ||
  "https://gallery.pitics.com";

const resolveSellerImageUrl = (value) => {
  if (!value) return "";
  const raw = typeof value === "string"
    ? value
    : value.url || value.downloadURL || value.downloadUrl || value.imageUrl ||
      value.imageURL || value.src || value.path || value.imagePath ||
      value.storagePath || value.fullPath || value.filePath || value.image || "";
  if (!raw || typeof raw !== "string") return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) return raw;
  // Firebase Storage object paths are converted to their public download URL.
  const storagePath = raw.startsWith("gs://") ? raw.replace(/^gs:\/\/[^/]+\//, "") : raw;
  return `https://firebasestorage.googleapis.com/v0/b/piticsjgat.firebasestorage.app/o/${encodeURIComponent(storagePath)}?alt=media`;
};

const parseProductImageList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === "object") {
        if (Array.isArray(parsed.images)) return parsed.images;
        if (Array.isArray(parsed.items)) return parsed.items;
      }
    } catch {
      // A plain URL/path is a single image, not a JSON array.
      return [value];
    }
    return [value];
  }
  if (value && typeof value === "object") {
    if (Array.isArray(value.images)) return value.images;
    if (Array.isArray(value.items)) return value.items;
    return [value];
  }
  return [];
};

const getProductGallery = (product) => {
  if (!product) return [];

  // The backend now returns a canonical, already de-duplicated gallery.
  // Prefer exactly one canonical source; never concatenate its compatibility
  // aliases (galleryImages/productImages/extraImages), as they overlap.
  const canonicalSource =
    (Array.isArray(product.imageGallery) && product.imageGallery.length && product.imageGallery) ||
    (Array.isArray(product.galleryImages) && product.galleryImages.length && product.galleryImages) ||
    (Array.isArray(product.productImages) && product.productImages.length && product.productImages) ||
    null;

  let candidates = [];
  if (canonicalSource) {
    candidates = canonicalSource;
  } else {
    const primary = product.productImagePath || product.productImage ||
      product.primaryImage || product.mainImage || product.image;
    if (primary) candidates.push(primary);

    // Legacy fallback: choose only the first populated gallery array.
    const legacyFields = [
      "extraFilePaths", "extraImagePaths", "extraImages",
      "additionalImages", "additionalImagePaths", "productImageUrls",
      "imagePaths", "imageUrls", "gallery", "images"
    ];
    const legacyField = legacyFields.find((field) => {
      const value = product[field];
      return (Array.isArray(value) && value.length) ||
        (typeof value === "string" && value.trim());
    });
    if (legacyField) candidates.push(...parseProductImageList(product[legacyField]));
  }

  let orientations = product.imageOrientations || product.imageOrientation || [];
  if (typeof orientations === "string") {
    try { orientations = JSON.parse(orientations); } catch { orientations = []; }
  }
  if (!Array.isArray(orientations)) orientations = [];

  const normalizeKey = (value) => {
    let key = String(value || "").trim();
    if (!key) return "";
    // Treat Firebase download URLs and raw Storage paths as the same asset.
    if (/^https?:\/\//i.test(key)) {
      try {
        const parsed = new URL(key);
        const marker = "/o/";
        const at = parsed.pathname.indexOf(marker);
        if (at >= 0) key = decodeURIComponent(parsed.pathname.slice(at + marker.length));
        else key = `${parsed.origin}${parsed.pathname}`;
      } catch (_) {
        key = key.split("?")[0];
      }
    }
    return key.replace(/^gs:\/\/[^/]+\//, "").replace(/^\/+|\/+$/g, "");
  };

  const seen = new Set();
  const gallery = [];
  candidates.forEach((item, index) => {
    if (!item) return;
    const path = typeof item === "string"
      ? item
      : item.path || item.imagePath || item.storagePath || item.fullPath ||
        item.filePath || item.imageUrl || item.imageURL || item.url ||
        item.downloadURL || item.downloadUrl || item.src || item.image || "";
    const url = resolveSellerImageUrl(item);
    const key = normalizeKey(path);
    if (!key || !url || seen.has(key)) return;
    seen.add(key);

    gallery.push({
      path: String(path).trim(),
      url,
      orientation: (typeof item === "object" && item.orientation) ||
        orientations[index] || "square",
      isExisting: true
    });
  });
  return gallery;
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  outline: "none",
  marginBottom: 14,
  fontSize: 14,
  boxSizing: "border-box"
};

// Keep the seller catalogue badge visually consistent with the buyer catalogue.
// Support the common aggregate field names used by product/rating APIs.
const getProductRating = (product) => {
  const average = Number(
    product?.ratingAverage ??
    product?.averageRating ??
    product?.ratingAvg ??
    product?.rating ??
    0
  );
  const count = Number(
    product?.ratingCount ??
    product?.reviewCount ??
    product?.reviewsCount ??
    0
  );
  return {
    average: Number.isFinite(average) ? Math.min(5, Math.max(0, average)) : 0,
    count: Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
  };
};

function ProductRatingBadge({ product, compact = false }) {
  const { average, count } = getProductRating(product);
  if (average <= 0 || count <= 0) return null;

  return (
    <div
      title={`${average.toFixed(1)} out of 5 from ${count} ${count === 1 ? "rating" : "ratings"}`}
      aria-label={`Rated ${average.toFixed(1)} out of 5, ${count} ratings`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: compact ? "4px 6px" : "5px 8px",
        borderRadius: 999,
        background: "rgba(255,255,255,.97)",
        color: "#111827",
        border: "1px solid rgba(15,23,42,.10)",
        boxShadow: "0 2px 8px rgba(0,0,0,.16)",
        fontSize: compact ? 11 : 12,
        lineHeight: 1.2,
        fontWeight: 800,
        whiteSpace: "nowrap"
      }}
    >
      <span>{average.toFixed(1)}</span>
      <span style={{ color: "#f59e0b", fontSize: compact ? 12 : 13 }}>★</span>
      <span style={{ color: "#64748b", fontWeight: 700 }}>({count})</span>
    </div>
  );
}

const editLabel = { display: "block", color: "#334155", fontSize: 12, fontWeight: 850 };
const editCheck = { display: "inline-flex", alignItems: "center", gap: 7, color: "#334155", fontSize: 13, fontWeight: 800, cursor: "pointer" };

export default function SellerProducts() {

  const [products, setProducts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [search, setSearch] =
    useState("");

  const [
    selectedProduct,
    setSelectedProduct
  ] = useState(null);

  const [
    editingProduct,
    setEditingProduct
  ] = useState(null);

  const [saving, setSaving] =
    useState(false);

  const [youtubeShortsEnabled, setYoutubeShortsEnabled] = useState(false);


  const [showAddModal, setShowAddModal] = useState(false);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const emptyProductForm = {
    name: "", description: "", category: "", subcategory: "", subsubcategory: "",
    sku: "", brand: "", hsn: "", weight: "", mrp: "", sellingPrice: "",
    stockQty: "", minOrderQty: 1, orderStep: 1, requiresName: false,
    requiresPhoto: false, minPhotos: 0, maxPhotos: 0, youtubeShortUrl: "",
    productImage: null, productPreviewImage: null, productImageOrientation: "square", freeProductOffer: false,
    shareGiftEnabled: false, shareGiftProductName: "", shareGiftProductPrice: "", shareGiftProductQty: "", shareGiftImage: null,
    returnAllowed: false, replacementAllowed: false, returnWindowDays: 7,
    isBundle: false, bundleQuantity: 2, productOptions: [], variants: []
  };

  const [newProduct, setNewProduct] = useState(emptyProductForm);

  const parseOptions = (product) =>
    Array.isArray(product?.productOptions)
      ? product.productOptions.map((o) => ({
          name: o?.name || "",
          values: Array.isArray(o?.values) ? [...o.values] : []
        }))
      : [];

  const makeVariantRows = (options, existing = []) => {
    if (!options.length) return [];
    if (!options.every(o => o.name.trim() && o.values.some(v => String(v).trim()))) return [];

    let combinations = [{}];
    for (const option of options.map(o => ({
      name: o.name.trim(),
      values: [...new Set(o.values.map(v => String(v).trim()).filter(Boolean))].slice(0, 50)
    }))) {
      combinations = combinations.flatMap(row =>
        option.values.map(value => ({ ...row, [option.name]: value }))
      );
    }

    return combinations.map(combo => {
      const old = existing.find(v =>
        Object.keys(combo).every(key =>
          String(v?.options?.[key] ?? "").toLowerCase() === String(combo[key]).toLowerCase()
        )
      );
      return {
        variantId: old?.variantId || "",
        options: combo,
        sku: old?.sku || "",
        mrp: old?.mrp ?? "",
        sellingPrice: old?.sellingPrice ?? "",
        stockQty: old?.stockQty ?? "",
        minOrderQty: old?.minOrderQty ?? 1,
        orderStep: old?.orderStep ?? 1,
        active: old?.active !== false
      };
    });
  };

  const QUICK_OPTION_PRESETS = {
    Size: ["XS", "S", "M", "L", "XL", "XXL", "XXXL"],
    Color: ["Black", "White", "Red", "Blue", "Green", "Yellow", "Pink", "Purple", "Orange", "Brown", "Grey"],
    Material: ["Cotton", "Polyester", "Leather", "Metal", "Wood"],
    Storage: ["32 GB", "64 GB", "128 GB", "256 GB", "512 GB", "1 TB"],
    Style: ["Regular", "Slim", "Classic", "Modern"]
  };

  const addOption = (setter, presetName = "") => setter(prev => {
    if (prev.productOptions.length >= 5) return prev;

    const existing = prev.productOptions.some(
      o => String(o?.name || "").trim().toLowerCase() === String(presetName || "").trim().toLowerCase()
    );

    if (presetName && existing) return prev;

    const presetValues = QUICK_OPTION_PRESETS[presetName] || [""];
    return {
      ...prev,
      productOptions: [
        ...prev.productOptions,
        { name: presetName, values: [...presetValues] }
      ],
      variants: []
    };
  });

  const addCustomOption = (setter) => addOption(setter, "");

  const optionAlreadyExists = (options, index, name) =>
    options.some((o, i) =>
      i !== index &&
      String(o?.name || "").trim().toLowerCase() === String(name || "").trim().toLowerCase()
    );

  const removeOption = (setter, index) => setter(prev => ({
    ...prev,
    productOptions: prev.productOptions.filter((_, i) => i !== index),
    variants: []
  }));

  const updateOption = (setter, index, name) => setter(prev => {
    const options = [...prev.productOptions];
    if (optionAlreadyExists(options, index, name)) {
      alert(`Option "${name.trim()}" is already added. Each option must be unique.`);
      return prev;
    }
    options[index] = { ...options[index], name };
    return { ...prev, productOptions: options, variants: [] };
  });

  const addOptionValue = (setter, optionIndex) => setter(prev => {
    const options = [...prev.productOptions];
    const currentValues = options[optionIndex]?.values || [];
    if (currentValues.length >= 50) {
      alert("An option can have a maximum of 50 values.");
      return prev;
    }
    options[optionIndex] = {
      ...options[optionIndex],
      values: [...currentValues, ""]
    };
    return { ...prev, productOptions: options, variants: [] };
  });

  const updateOptionValue = (setter, optionIndex, valueIndex, value) => setter(prev => {
    const options = [...prev.productOptions];
    const values = [...options[optionIndex].values];
    values[valueIndex] = value;
    options[optionIndex] = { ...options[optionIndex], values };
    return { ...prev, productOptions: options, variants: [] };
  });

  const removeOptionValue = (setter, optionIndex, valueIndex) => setter(prev => {
    const options = [...prev.productOptions];
    options[optionIndex] = {
      ...options[optionIndex],
      values: options[optionIndex].values.filter((_, i) => i !== valueIndex)
    };
    return { ...prev, productOptions: options, variants: [] };
  });

  const generateRows = (form, setter) => {
    const rows = makeVariantRows(form.productOptions, form.variants);
    if (form.productOptions.length && !rows.length) {
      alert("Enter an option name and at least one value for every option.");
      return;
    }
    if (rows.length > 250) {
      alert("The selected options create more than 250 variants.");
      return;
    }
    setter(prev => ({ ...prev, variants: rows }));
  };

  const updateVariantField = (setter, index, field, value) => setter(prev => {
    const variants = [...prev.variants];
    variants[index] = { ...variants[index], [field]: value };
    return { ...prev, variants };
  });

  const loadAllowedCategories = async () => {
    try {
      setLoadingCategories(true);
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API}/api/seller/allowed-categories`, {
        headers: { "X-Firebase-Authorization": token }
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to load categories");
      setAllowedCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const createProduct = async () => {
    try {
      if (!newProduct.name.trim() || !newProduct.category) {
        alert("Product name and category are required.");
        return;
      }
      if (!newProduct.productImage) {
        alert("Product image is required.");
        return;
      }
      if (newProduct.shareGiftEnabled) {
        const giftName = String(newProduct.shareGiftProductName || "").trim();
        const giftPrice = Number(newProduct.shareGiftProductPrice);
        const giftQty = Number(newProduct.shareGiftProductQty);
        if (!giftName || giftName.length > 150) {
          alert("Enter a valid free gift product name (maximum 150 characters).");
          return;
        }
        if (!Number.isFinite(giftPrice) || giftPrice <= 0) {
          alert("Enter a valid free gift price greater than zero.");
          return;
        }
        if (!Number.isInteger(giftQty) || giftQty < 1) {
          alert("Free gift quantity must be a whole number of at least 1.");
          return;
        }
        if (!newProduct.shareGiftImage) {
          alert("Upload a preview image for the free gift.");
          return;
        }
      }
      if (!newProduct.returnAllowed && !newProduct.replacementAllowed) {
        alert("Enable at least one option: Returns or Replacement. This is mandatory for every product.");
        return;
      }
      const createPolicyWindow = Number(newProduct.returnWindowDays);
      if (!Number.isInteger(createPolicyWindow) || createPolicyWindow < 1 || createPolicyWindow > 90) {
        alert("Return / replacement window must be between 1 and 90 days.");
        return;
      }

      const hasOptions = newProduct.productOptions.length > 0;
      const variants = hasOptions
        ? makeVariantRows(newProduct.productOptions, newProduct.variants)
        : [];

      if (!newProduct.subcategory) {
        alert("Select a subcategory.");
        return;
      }

      const isAllProducts =
        String(newProduct.category || "")
          .replace(/[^a-z0-9]/gi, "")
          .toLowerCase() === "allproducts";

      if (isAllProducts && !newProduct.subsubcategory) {
        alert("Select Subcategory 2 for All Products.");
        return;
      }

      const baseMrp = Number(newProduct.mrp);
      const basePrice = Number(newProduct.sellingPrice);
      const baseStock = Math.floor(Number(newProduct.stockQty));
      const baseMin = Math.max(1, Math.floor(Number(newProduct.minOrderQty) || 1));
      const baseStep = Math.max(1, Math.floor(Number(newProduct.orderStep) || 1));

      if (!Number.isFinite(baseMrp) || baseMrp <= 0) {
        alert("MRP must be greater than 0.");
        return;
      }
      if (!Number.isFinite(basePrice) || basePrice <= 0 || basePrice > baseMrp) {
        alert("Selling price must be valid and cannot exceed MRP.");
        return;
      }
      if (!Number.isFinite(baseStock) || baseStock < baseMin) {
        alert("Stock must be at least Minimum Order Qty.");
        return;
      }
      if ((baseStock - baseMin) % baseStep !== 0) {
        alert("Stock is invalid for the selected Order Step.");
        return;
      }

      if (hasOptions && !variants.length) {
        alert("Generate valid variants before saving.");
        return;
      }

      if (hasOptions) {
        for (let i = 0; i < variants.length; i++) {
          const v = variants[i];
          const mrp = Number(v.mrp);
          const price = Number(v.sellingPrice);
          const stock = Math.floor(Number(v.stockQty));
          const min = Math.max(1, Math.floor(Number(v.minOrderQty) || 1));
          const step = Math.max(1, Math.floor(Number(v.orderStep) || 1));

          if (!String(v.sku || "").trim()) {
            alert(`Variant ${i + 1}: SKU is required.`);
            return;
          }
          if (!Number.isFinite(mrp) || mrp <= 0 || !Number.isFinite(price) || price <= 0 || price > mrp) {
            alert(`Variant ${i + 1}: invalid MRP or selling price.`);
            return;
          }
          if (!Number.isFinite(stock) || stock < min || (stock - min) % step !== 0) {
            alert(`Variant ${i + 1}: invalid stock/minimum/order step.`);
            return;
          }
        }
      }

      const payload = {
        name: newProduct.name.trim(),
        description: newProduct.description,
        category: newProduct.category,
        subcategory: newProduct.subcategory,
        subsubcategory: newProduct.subsubcategory,
        sku: newProduct.sku,
        brand: newProduct.brand,
        hsn: newProduct.hsn,
        weight: newProduct.weight,
        mrp: Number(newProduct.mrp) || 0,
        sellingPrice: Number(newProduct.sellingPrice) || 0,
        stockQty: Number(newProduct.stockQty) || 0,
        minOrderQty: Math.max(1, Math.floor(Number(newProduct.minOrderQty) || 1)),
        orderStep: Math.max(1, Math.floor(Number(newProduct.orderStep) || 1)),
        requiresName: Boolean(newProduct.requiresName),
        requiresPhoto: Boolean(newProduct.requiresPhoto),
        minPhotos: Number(newProduct.minPhotos) || 0,
        maxPhotos: Number(newProduct.maxPhotos) || 0,
        returnAllowed: Boolean(newProduct.returnAllowed),
        replacementAllowed: Boolean(newProduct.replacementAllowed),
        returnWindowDays: createPolicyWindow,
        youtubeShortUrl: youtubeShortsEnabled ? newProduct.youtubeShortUrl : "",
        isBundle: Boolean(newProduct.isBundle),
        freeProductOffer: false,
        shareGiftEnabled: Boolean(newProduct.shareGiftEnabled),
        shareGiftProductName: newProduct.shareGiftEnabled ? String(newProduct.shareGiftProductName || "").trim() : "",
        shareGiftProductPrice: newProduct.shareGiftEnabled ? Number(newProduct.shareGiftProductPrice) : 0,
        shareGiftProductQty: newProduct.shareGiftEnabled ? Math.floor(Number(newProduct.shareGiftProductQty)) : 0,
        bundleQuantity: Math.max(2, Math.floor(Number(newProduct.bundleQuantity) || 2)),
        imageOrientations: JSON.stringify([newProduct.productImageOrientation || "square"]),
        hasVariants: hasOptions,
        productOptions: hasOptions ? newProduct.productOptions : [],
        variants
      };

      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      formData.append("productImage", newProduct.productImage);
      if (newProduct.productPreviewImage) formData.append("productPreviewImage", newProduct.productPreviewImage);
      if (newProduct.shareGiftEnabled && newProduct.shareGiftImage) formData.append("shareGiftImage", newProduct.shareGiftImage);

      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${API}/api/seller/create-product`, {
        method: "POST",
        headers: { "X-Firebase-Authorization": token },
        body: formData
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Failed to create product");

      alert("Product added successfully.");
      setShowAddModal(false);
      setNewProduct({ ...emptyProductForm, productOptions: [], variants: [] });
      await loadProducts();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to create product");
    }
  };

  /* =====================================
     LOAD PRODUCTS
  ===================================== */

  const loadProducts =
    async () => {

      try {

        setLoading(true);

        const token =
          await auth.currentUser.getIdToken();

        const res =
          await fetch(
            `${API}/api/seller/products`,
            {
              headers: {
                "X-Firebase-Authorization":
                  token
              }
            }
          );

        const data =
          await res.json();

        if (!data.success) {

          throw new Error(
            data.error ||
            "Failed to load products"
          );
        }

        setProducts(
          Array.isArray(
            data.products
          )
            ? data.products
            : []
        );

      } catch (err) {

        console.error(err);

        alert(
          err.message ||
          "Failed to load products"
        );

      } finally {

        setLoading(false);
      }
    };

  useEffect(() => {
    loadProducts();
    loadAllowedCategories();
    (async () => {
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${API}/api/seller/me`, { headers: { "X-Firebase-Authorization": token } });
        const data = await res.json();
        if (data.success) setYoutubeShortsEnabled(Boolean(data.youtubeShortsEnabled));
      } catch (err) {
        console.error("Seller access load error", err);
      }
    })();
  }, []);

  /* =====================================
     DELETE PRODUCT
  ===================================== */

  const deleteProduct =
    async (docId) => {

      const ok =
        window.confirm(
          "Delete this product?"
        );

      if (!ok) return;

      try {

        const token =
          await auth.currentUser.getIdToken();

        const res =
          await fetch(
            `${API}/api/seller/delete-product`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                "X-Firebase-Authorization":
                  token
              },

              body: JSON.stringify({
                docId
              })
            }
          );

        const data =
          await res.json();

        if (!data.success) {

          throw new Error(
            data.error ||
            "Delete failed"
          );
        }

        alert(
          "Product deleted"
        );

        setSelectedProduct(
          null
        );

        loadProducts();

      } catch (err) {

        console.error(err);

        alert(
          err.message ||
          "Delete failed"
        );
      }
    };

  /* =====================================
     UPDATE PRODUCT
  ===================================== */

  const updateProduct =
    async () => {

      try {

        setSaving(true);

        const token =
          await auth.currentUser.getIdToken();

        if (!String(editingProduct.name || "").trim() || !String(editingProduct.category || "").trim()) {
          alert("Product name and category are required.");
          return;
        }
        if (!editingProduct.returnAllowed && !editingProduct.replacementAllowed) {
          alert("Enable at least one option: Returns or Replacement. This is mandatory for every product.");
          return;
        }
        const editPolicyWindow = Number(editingProduct.returnWindowDays);
        if (!Number.isInteger(editPolicyWindow) || editPolicyWindow < 1 || editPolicyWindow > 90) {
          alert("Return / replacement window must be between 1 and 90 days.");
          return;
        }
        if (Number(editingProduct.sellingPrice) <= 0 || Number(editingProduct.mrp) <= 0 || Number(editingProduct.sellingPrice) > Number(editingProduct.mrp)) {
          alert("Enter a valid selling price and MRP. Selling price cannot exceed MRP.");
          return;
        }
        if ((editingProduct.galleryImages || []).length + (editingProduct.newGalleryFiles || []).length === 0 && !editingProduct.newImage) {
          alert("Keep at least one product image.");
          return;
        }

        // Gift details belong to this product listing; no second catalogue
        // product ID is selected or required.
        if (editingProduct.shareGiftEnabled) {
          const giftName = String(editingProduct.shareGiftProductName || "").trim();
          const giftPrice = Number(editingProduct.shareGiftProductPrice);
          const giftQty = Number(editingProduct.shareGiftProductQty);
          if (!giftName || giftName.length > 150) {
            alert("Enter a valid free gift product name (maximum 150 characters).");
            return;
          }
          if (!Number.isFinite(giftPrice) || giftPrice <= 0) {
            alert("Enter a valid free gift price greater than zero.");
            return;
          }
          if (!Number.isInteger(giftQty) || giftQty < 1) {
            alert("Free gift quantity must be a whole number of at least 1.");
            return;
          }
          if (!(editingProduct.shareGiftImage instanceof File) && !editingProduct.shareGiftImagePath) {
            alert("Upload a preview image for the free gift.");
            return;
          }
        }

        const formData =
          new FormData();

        formData.append(
          "payload",
          JSON.stringify({

            docId:
              editingProduct.docId,

            data: {

              name:
                editingProduct.name,

              description:
                editingProduct.description,

              category: editingProduct.category || "",
              subcategory: editingProduct.subcategory || "",
              subsubcategory: editingProduct.subsubcategory || "",
              sku: editingProduct.sku || "",
              brand: editingProduct.brand || "",
              hsn: editingProduct.hsn || "",
              weight: editingProduct.weight || "",
              isBundle: Boolean(editingProduct.isBundle),
              bundleQuantity: Math.max(2, Math.floor(Number(editingProduct.bundleQuantity) || 2)),
              minPhotos: Number(editingProduct.minPhotos) || 0,
              maxPhotos: Number(editingProduct.maxPhotos) || 0,
              returnAllowed: Boolean(editingProduct.returnAllowed),
              replacementAllowed: Boolean(editingProduct.replacementAllowed),
              returnWindowDays: editPolicyWindow,
              existingImages: (editingProduct.galleryImages || []).filter(x => x.isExisting).map(x => ({
                path: x.path, orientation: x.orientation || "square"
              })),
              primaryImagePath: (editingProduct.galleryImages || []).find(x => x.isExisting)?.path || "",
              productPreviewImagePath: editingProduct.removePreviewImage ? "" : (editingProduct.productPreviewImagePath || editingProduct.previewImagePath || ""),
              removePreviewImage: Boolean(editingProduct.removePreviewImage),
              removeImagePaths: editingProduct.removeImagePaths || [],

              sellingPrice:
                Number(
                  editingProduct.sellingPrice
                ),

              stockQty:
                Number(
                  editingProduct.stockQty
                ),

              mrp:
                Number(
                  editingProduct.mrp
                ),

              minOrderQty:
                Math.max(
                  1,
                  Math.floor(
                    Number(editingProduct.minOrderQty) || 1
                  )
                ),

              orderStep:
                Math.max(
                  1,
                  Math.floor(
                    Number(editingProduct.orderStep) || 1
                  )
                ),

              requiresName:
                Boolean(
                  editingProduct.requiresName
                ),

              requiresPhoto:
                Boolean(
                  editingProduct.requiresPhoto
                ),

              hasVariants: Array.isArray(editingProduct.productOptions)
                && editingProduct.productOptions.length > 0,
              productOptions: Array.isArray(editingProduct.productOptions)
                ? editingProduct.productOptions
                : [],
              variants: Array.isArray(editingProduct.variants)
                ? editingProduct.variants
                : [],

              ...(youtubeShortsEnabled
                ? { youtubeShortUrl: editingProduct.youtubeShortUrl || "" }
                : {}),

              freeProductOffer: false,
              shareGiftEnabled: Boolean(editingProduct.shareGiftEnabled),
              shareGiftProductName: editingProduct.shareGiftEnabled ? String(editingProduct.shareGiftProductName || "").trim() : "",
              shareGiftProductPrice: editingProduct.shareGiftEnabled ? Number(editingProduct.shareGiftProductPrice) : 0,
              shareGiftProductQty: editingProduct.shareGiftEnabled ? Math.floor(Number(editingProduct.shareGiftProductQty)) : 0,
              imageOrientations: JSON.stringify([
                ...(editingProduct.galleryImages || []).map(x => x.orientation || "square"),
                ...(editingProduct.newGalleryFiles || []).map(x => x.orientation || "square")
              ])
            }
          })
        );

        if (editingProduct.newPreviewImage) {
          formData.append("productPreviewImage", editingProduct.newPreviewImage);
        }
        if (editingProduct.shareGiftImage instanceof File) {
          formData.append("shareGiftImage", editingProduct.shareGiftImage);
        }

        const retainedImages = (editingProduct.galleryImages || []).filter(x => x.isExisting);
        const addedImages = editingProduct.newGalleryFiles || [];
        if (editingProduct.newImage) {
          formData.append("productImage", editingProduct.newImage);
        } else if (retainedImages.length === 0 && addedImages[0]?.file) {
          formData.append("productImage", addedImages[0].file);
        }
        addedImages.forEach((item, index) => {
          if (item?.file && !(retainedImages.length === 0 && index === 0)) {
            formData.append("extraFile", item.file);
          }
        });

        const res =
          await fetch(
            `${API}/api/seller/update-product`,
            {
              method: "POST",

              headers: {
                "X-Firebase-Authorization":
                  token
              },

              body: formData
            }
          );

        const data =
          await res.json();

        if (!data.success) {

          throw new Error(
            data.error ||
            "Update failed"
          );
        }

        alert(
          "Product updated successfully"
        );

        setEditingProduct(
          null
        );

        setSelectedProduct(
          null
        );

        loadProducts();

      } catch (err) {

        console.error(err);

        alert(
          err.message ||
          "Update failed"
        );

      } finally {

        setSaving(false);
      }
    };

  /* =====================================
     SEARCH FILTER
  ===================================== */

  const filteredProducts =
    useMemo(() => {

      const q =
        search
          .trim()
          .toLowerCase();

      if (!q)
        return products;

      return products.filter((p) => {

        const text = `
          ${p.name || ""}
          ${p.category || ""}
          ${p.subcategory || ""}
          ${p.collectionName || ""}
          ${p.productId || ""}
        `
          .toLowerCase();

        return text.includes(q);
      });

    }, [products, search]);

  /* =====================================
     LOADING
  ===================================== */

  if (loading) {

    return (
      <div
        style={{
          padding: 30,
          fontWeight: 800
        }}
      >
        Loading products...
      </div>
    );
  }

  /* =====================================
     UI
  ===================================== */

  return (

    <div>

      {/* =====================================
         TOP BAR
      ===================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 24
        }}
      >

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span
            style={{
              color: "#64748b",
              fontSize: 13,
              fontWeight: 800
            }}
          >
            {products.length} listed products
          </span>
        </div>

        <input
          value={search}
          onChange={(e) =>
            setSearch(
              e.target.value
            )
          }
          placeholder="Search products..."
          style={{
            padding:
              "12px 16px",

            borderRadius: 14,

            border:
              "1px solid #d1d5db",

            minWidth: 260,

            outline: "none"
          }}
        />

      </div>

      {/* =====================================
         EMPTY
      ===================================== */}

      {filteredProducts.length === 0 && (

        <div
          style={{
            padding: 24,
            background:
              "#fff",
            borderRadius: 24,
            fontWeight: 700
          }}
        >
          No products found
        </div>
      )}

      {/* =====================================
         SIMPLE PRODUCT LIST
      ===================================== */}

      <div
        style={{
          display: "flex",
          flexDirection:
            "column",
          gap: 14
        }}
      >

        {filteredProducts.map(
          (p, index) => {

            let imageUrl =
  p.productImagePath || "";

if (
  imageUrl &&
  !imageUrl.startsWith("http")
) {

  imageUrl =
    `https://firebasestorage.googleapis.com/v0/b/piticsjgat.firebasestorage.app/o/${encodeURIComponent(imageUrl)}?alt=media`;
}

if (!imageUrl) {

  imageUrl =
    "https://via.placeholder.com/300x220?text=No+Image";
}

            return (

              <div
                key={p.docId}
                onClick={() =>
                  setSelectedProduct(
                    p
                  )
                }
                style={{
                  display: "flex",
                  alignItems:
                    "center",
                  gap: 10,
                  background: "linear-gradient(110deg,#ffffff,#f8faff)",
                  border: "1px solid #e8edff",
                  borderRadius: 16,
                  padding: "9px 12px",
                  cursor: "pointer",
                  boxShadow:
                    "0 6px 20px rgba(0,0,0,.06)"
                }}
              >

                {/* SERIAL */}

                <div
                  style={{
                    minWidth: 30,
                    fontWeight: 900
                  }}
                >
                  {index + 1}
                </div>

                {/* IMAGE */}

                <div style={{ position: "relative", flex: "0 0 58px", width: 58, height: 58 }}>
                  <img
                    src={imageUrl}
                    alt={p.name || "Product"}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/70";
                    }}
                    style={{
                      display: "block",
                      width: 58,
                      height: 58,
                      objectFit: "cover",
                      borderRadius: 16
                    }}
                  />
                  <div style={{ position: "absolute", top: 3, right: 3, zIndex: 1 }}>
                    <ProductRatingBadge product={p} compact />
                  </div>
                </div>

                {/* BASIC */}

                <div
                  style={{
                    flex: 1
                  }}
                >

                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 17
                    }}
                  >
                    {p.name}
                  </div>

                  <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                    Product Code: {p.productCode || p.sku || "Generated on listing"}
                  </div>

                  <div
                    style={{
                      marginTop: 6,
                      color: "#475569",
                      fontSize: 14
                    }}
                  >
                    Category:
                    {" "}
                    {p.category || "-"}
                  </div>

                  <div
                    style={{
                      marginTop: 2,
                      color: "#475569",
                      fontSize: 14
                    }}
                  >
                    Subcategory:
                    {" "}
                    {p.subcategory || "-"}
                  </div>

                </div>

                {/* PRICE */}

                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 18
                  }}
                >
                  ₹
                  {p.sellingPrice || 0}
                </div>

              </div>
            );
          }
        )}

      </div>

      {/* =====================================
         PRODUCT DETAILS MODAL
      ===================================== */}

      {selectedProduct && (

        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(0,0,0,.55)",
            zIndex: 9999,
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            padding: 20
          }}
        >

          <div
            style={{
              width: "100%",
              maxWidth: 800,
              maxHeight:
                "90vh",
              overflow: "auto",
              background:
                "#fff",
              borderRadius: 28,
              padding: 24
            }}
          >

            <div style={{ position: "relative", width: "100%", marginBottom: 8 }}>
              <img
                src={
                  selectedProduct.productImagePath?.startsWith("http")
                    ? selectedProduct.productImagePath
                    : selectedProduct.productImagePath
                      ? `https://firebasestorage.googleapis.com/v0/b/piticsjgat.firebasestorage.app/o/${encodeURIComponent(selectedProduct.productImagePath)}?alt=media`
                      : "https://via.placeholder.com/600x320?text=No+Image"
                }
                alt={selectedProduct.name || "Product"}
                style={{
                  display: "block",
                  width: "100%",
                  maxHeight: 320,
                  objectFit: "cover",
                  borderRadius: 22
                }}
              />
              <div style={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}>
                <ProductRatingBadge product={selectedProduct} />
              </div>
            </div>

            <h2>
              {selectedProduct.name}
            </h2>

            <div style={{ marginBottom: 8 }}>
              <b>Product Code:</b>{" "}
              {selectedProduct.productCode || selectedProduct.sku || "-"}
            </div>

            <div>
              <b>Category:</b>
              {" "}
              {selectedProduct.category || "-"}
            </div>

            <div>
              <b>Subcategory:</b>
              {" "}
              {selectedProduct.subcategory || "-"}
            </div>

            <div>
              <b>Description:</b>
              {" "}
              {selectedProduct.description || "-"}
            </div>

            <div>
              <b>Price:</b>
              {" "}
              ₹
              {selectedProduct.sellingPrice || 0}
            </div>

            <div>
              <b>MRP:</b>
              {" "}
              ₹
              {selectedProduct.mrp || 0}
            </div>

            <div style={{ marginTop: 8 }}>
              <b>Share &amp; Earn:</b> {selectedProduct.shareGiftEnabled ? "Enabled" : "Not enabled"}
              {selectedProduct.shareGiftEnabled && <span> · Gift: {selectedProduct.shareGiftProductName || "Configured"} · Value: ₹{Number(selectedProduct.shareGiftProductPrice || 0).toLocaleString("en-IN")} · Qty: {Number(selectedProduct.shareGiftProductQty || 0)}</span>}
            </div>

            <div>
              <b>Stock:</b>
              {" "}
              {selectedProduct.stockQty || 0}
            </div>

            {selectedProduct.hasVariants && Array.isArray(selectedProduct.productOptions) && (
              <div style={{
                marginTop: 16, padding: 16, background: "#f8fafc",
                borderRadius: 18, border: "1px solid #e2e8f0"
              }}>
                <b>Variants</b>
                <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {selectedProduct.productOptions.map(o => (
                    <span key={o.name} style={{
                      background: "#e2e8f0", padding: "6px 10px",
                      borderRadius: 999, fontSize: 13
                    }}>
                      {o.name}: {(o.values || []).join(", ")}
                    </span>
                  ))}
                </div>
                <div style={{ marginTop: 12, overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr>
                        {selectedProduct.productOptions.map(o =>
                          <th key={o.name} style={{ textAlign: "left", padding: 8 }}>{o.name}</th>
                        )}
                        <th style={{ textAlign: "left", padding: 8 }}>SKU</th>
                        <th style={{ textAlign: "right", padding: 8 }}>Price</th>
                        <th style={{ textAlign: "right", padding: 8 }}>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedProduct.variants || []).map(v => (
                        <tr key={v.variantId}>
                          {selectedProduct.productOptions.map(o =>
                            <td key={o.name} style={{ padding: 8, borderTop: "1px solid #e2e8f0" }}>
                              {v.options?.[o.name] || "-"}
                            </td>
                          )}
                          <td style={{ padding: 8, borderTop: "1px solid #e2e8f0" }}>{v.sku}</td>
                          <td style={{ padding: 8, textAlign: "right", borderTop: "1px solid #e2e8f0" }}>₹{v.sellingPrice || 0}</td>
                          <td style={{ padding: 8, textAlign: "right", borderTop: "1px solid #e2e8f0" }}>{v.stockQty || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div>
              <b>Product ID:</b>
              {" "}
              {selectedProduct.productId || "-"}
            </div>

            <div
              style={{
                marginTop: 14,
                display: "flex",
                gap: 10,
                flexWrap: "wrap"
              }}
            >

              {selectedProduct.requiresName && (
                <div>
                  ✍️ Name Upload
                </div>
              )}

              {selectedProduct.requiresPhoto && (
                <div>
                  📸 Photo Upload
                </div>
              )}

            </div>

            {/* ACTIONS */}

            <div
              style={{
                display: "flex",
                gap: 14,
                marginTop: 24
              }}
            >

              <button
                onClick={() =>
                  setEditingProduct({
                    ...selectedProduct,
                    category: selectedProduct.category || "",
                    subcategory: selectedProduct.subcategory || "",
                    subsubcategory: selectedProduct.subsubcategory || selectedProduct.subsubcategory2 || "",
                    sku: selectedProduct.sku || "",
                    brand: selectedProduct.brand || "",
                    hsn: selectedProduct.hsn || "",
                    weight: selectedProduct.weight || "",
                    isBundle: Boolean(selectedProduct.isBundle),
                    bundleQuantity: selectedProduct.bundleQuantity || 2,
                    minPhotos: selectedProduct.minPhotos ?? 0,
                    maxPhotos: selectedProduct.maxPhotos ?? 0,
                    returnAllowed: Boolean(selectedProduct.returnAllowed ?? selectedProduct.returnsAllowed),
                    replacementAllowed: Boolean(selectedProduct.replacementAllowed ?? selectedProduct.exchangeAllowed),
                    returnWindowDays: selectedProduct.returnWindowDays ?? 7,
                    galleryImages: getProductGallery(selectedProduct),
                    newGalleryFiles: [],
                    removeImagePaths: [],
                    productPreviewImagePath: selectedProduct.productPreviewImagePath || selectedProduct.previewImagePath || "",
                    productOptions: parseOptions(selectedProduct),
                    variants: Array.isArray(selectedProduct.variants)
                      ? selectedProduct.variants.map(v => ({
                          ...v,
                          options: { ...(v.options || {}) }
                        }))
                      : []
                  })
                }
                style={{
                  border: "none",
                  background:
                    "#2563eb",
                  color: "#fff",
                  padding:
                    "14px 18px",
                  borderRadius: 16,
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                ✏️ Edit
              </button>

              <button
                onClick={() =>
                  deleteProduct(
                    selectedProduct.docId
                  )
                }
                style={{
                  border: "none",
                  background:
                    "#ef4444",
                  color: "#fff",
                  padding:
                    "14px 18px",
                  borderRadius: 16,
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                🗑 Delete
              </button>

              <button
                onClick={() =>
                  setSelectedProduct(
                    null
                  )
                }
                style={{
                  border: "none",
                  background:
                    "#e2e8f0",
                  padding:
                    "14px 18px",
                  borderRadius: 16,
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                Close
              </button>

            </div>

          </div>

        </div>
      )}

      {/* =====================================
         EDIT MODAL
      ===================================== */}

      {editingProduct && (

        <div
          style={{
            position: "fixed",
            inset: 0,
            background:
              "rgba(0,0,0,.55)",
            zIndex: 10000,
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "center",
            padding: 20
          }}
        >

          <div
            style={{
              width: "100%",
              maxWidth: 1040,
              maxHeight:
                "90vh",
              overflow: "auto",
              background: "linear-gradient(145deg,#ffffff,#f7f9ff)",
              border: "1px solid #dbeafe",
              boxShadow: "0 24px 80px rgba(15,23,42,.28)",
              borderRadius: 24,
              padding: 24
            }}
          >

            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,marginBottom:18,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:11,fontWeight:900,letterSpacing:1.5,color:"#6366f1",textTransform:"uppercase"}}>PITICS Seller Studio</div>
                <h2 style={{margin:"4px 0 0",fontSize:24,color:"#172554"}}>✏️ Edit Product</h2>
                <div style={{fontSize:12,color:"#64748b",marginTop:4}}>Update product details, images, variants and customer policies.</div>
              </div>
              <span style={{padding:"7px 12px",borderRadius:999,background:"#eef2ff",color:"#4338ca",fontSize:12,fontWeight:800}}>Product ID · {editingProduct.productId || editingProduct.docId || "-"}</span>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12}}>
              <label style={editLabel}>Product name *
                <input required value={editingProduct.name || ""} onChange={e=>setEditingProduct(p=>({...p,name:e.target.value}))} placeholder="Product name" style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Category *
                <select required value={editingProduct.category || ""} onChange={e=>setEditingProduct(p=>({...p,category:e.target.value,subcategory:"",subsubcategory:""}))} style={{...inputStyle,marginTop:5}}>
                  <option value="">Select category</option>
                  {allowedCategories.map((c,i)=><option key={c.category || c.id || i} value={c.category || c.name}>{c.category || c.name}</option>)}
                </select>
              </label>
              <label style={editLabel}>Subcategory
                <select value={editingProduct.subcategory || ""} onChange={e=>setEditingProduct(p=>({...p,subcategory:e.target.value,subsubcategory:""}))} style={{...inputStyle,marginTop:5}}>
                  <option value="">Select subcategory</option>
                  {(allowedCategories.find(c=>String(c.category||c.name).toLowerCase()===String(editingProduct.category||"").toLowerCase())?.subcategories || []).map((sub,i)=><option key={i} value={typeof sub==="string"?sub:(sub.name||sub.category)}>{typeof sub==="string"?sub:(sub.name||sub.category)}</option>)}
                </select>
              </label>
              <label style={editLabel}>Subcategory 2
                {String(editingProduct.category || "").replace(/[^a-z0-9]/gi,"").toLowerCase()==="allproducts" ? (
                  <select value={editingProduct.subsubcategory || ""} onChange={e=>setEditingProduct(p=>({...p,subsubcategory:e.target.value}))} style={{...inputStyle,marginTop:5}}>
                    <option value="">Select Subcategory 2</option>
                    {((allowedCategories.find(c=>String(c.category||c.name).toLowerCase()===String(editingProduct.category||"").toLowerCase())?.subsubcategoryMap || {})[String(editingProduct.subcategory||"").toLowerCase()] || []).map((v,i)=><option key={i} value={v}>{v}</option>)}
                  </select>
                ) : <input value={editingProduct.subsubcategory || ""} onChange={e=>setEditingProduct(p=>({...p,subsubcategory:e.target.value}))} placeholder="Optional" style={{...inputStyle,marginTop:5}}/>}
              </label>
              <label style={editLabel}>SKU
                <input value={editingProduct.sku || ""} onChange={e=>setEditingProduct(p=>({...p,sku:e.target.value}))} placeholder="SKU" style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Brand
                <input value={editingProduct.brand || ""} onChange={e=>setEditingProduct(p=>({...p,brand:e.target.value}))} placeholder="Brand" style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>HSN
                <input value={editingProduct.hsn || ""} onChange={e=>setEditingProduct(p=>({...p,hsn:e.target.value}))} placeholder="HSN code" style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Weight
                <input value={editingProduct.weight || ""} onChange={e=>setEditingProduct(p=>({...p,weight:e.target.value}))} placeholder="Weight (e.g. 250 g)" style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>MRP (₹) *
                <input required type="number" min="0.01" step="0.01" value={editingProduct.mrp ?? ""} onChange={e=>setEditingProduct(p=>({...p,mrp:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Selling price (₹) *
                <input required type="number" min="0.01" step="0.01" value={editingProduct.sellingPrice ?? ""} onChange={e=>setEditingProduct(p=>({...p,sellingPrice:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Available stock
                <input type="number" min="0" value={editingProduct.stockQty ?? 0} onChange={e=>setEditingProduct(p=>({...p,stockQty:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Minimum order quantity
                <input type="number" min="1" value={editingProduct.minOrderQty ?? 1} onChange={e=>setEditingProduct(p=>({...p,minOrderQty:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Order step
                <input type="number" min="1" value={editingProduct.orderStep ?? 1} onChange={e=>setEditingProduct(p=>({...p,orderStep:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
            </div>
            <label style={{...editLabel,display:"block",marginTop:4}}>Description
              <textarea value={editingProduct.description || ""} onChange={e=>setEditingProduct(p=>({...p,description:e.target.value}))} rows={4} placeholder="Describe your product..." style={{...inputStyle,marginTop:5,resize:"vertical"}}/>
            </label>
            <div style={{display:"flex",gap:16,flexWrap:"wrap",padding:"12px 14px",borderRadius:14,background:"#f0f9ff",border:"1px solid #bae6fd",margin:"10px 0 18px"}}>
              <label style={editCheck}><input type="checkbox" checked={Boolean(editingProduct.isBundle)} onChange={e=>setEditingProduct(p=>({...p,isBundle:e.target.checked}))}/> Bundle product</label>
              {editingProduct.isBundle && <label style={editLabel}>Bundle quantity <input type="number" min="2" value={editingProduct.bundleQuantity ?? 2} onChange={e=>setEditingProduct(p=>({...p,bundleQuantity:e.target.value}))} style={{...inputStyle,width:110,margin:"4px 0 0"}}/></label>}
              <label style={editCheck}><input type="checkbox" checked={Boolean(editingProduct.requiresName)} onChange={e=>setEditingProduct(p=>({...p,requiresName:e.target.checked}))}/> Requires customer name</label>
              <label style={editCheck}><input type="checkbox" checked={Boolean(editingProduct.requiresPhoto)} onChange={e=>setEditingProduct(p=>({...p,requiresPhoto:e.target.checked}))}/> Requires customer photo</label>
            </div>
            {editingProduct.requiresPhoto && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:10,margin:"-6px 0 16px",padding:12,borderRadius:12,background:"#fff7ed",border:"1px solid #fed7aa"}}>
              <label style={editLabel}>Minimum customer photos
                <input type="number" min="1" max="12" value={editingProduct.minPhotos ?? 1} onChange={e=>setEditingProduct(p=>({...p,minPhotos:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
              <label style={editLabel}>Maximum customer photos
                <input type="number" min="1" max="12" value={editingProduct.maxPhotos ?? 1} onChange={e=>setEditingProduct(p=>({...p,maxPhotos:e.target.value}))} style={{...inputStyle,marginTop:5}}/>
              </label>
            </div>}
            <section style={{padding:16,borderRadius:16,background:"linear-gradient(120deg,#f0fdf4,#ecfeff)",border:"1px solid #a7f3d0",marginBottom:16}}>
              <div style={{fontWeight:900,color:"#065f46",marginBottom:10}}>↩️ Customer return & replacement policy</div>
              <div style={{display:"flex",gap:18,flexWrap:"wrap",alignItems:"center"}}>
                <label style={editCheck}><input type="checkbox" checked={Boolean(editingProduct.returnAllowed)} onChange={e=>setEditingProduct(p=>({...p,returnAllowed:e.target.checked}))}/> Returns allowed</label>
                <label style={editCheck}><input type="checkbox" checked={Boolean(editingProduct.replacementAllowed)} onChange={e=>setEditingProduct(p=>({...p,replacementAllowed:e.target.checked}))}/> Replacement allowed</label>
                <label style={editLabel}>Return / replacement window (days)
                  <input type="number" min="0" max="90" value={editingProduct.returnWindowDays ?? 7} onChange={e=>setEditingProduct(p=>({...p,returnWindowDays:e.target.value}))} style={{...inputStyle,width:120,margin:"4px 0 0"}}/>
                </label>
              </div>
              <small style={{display:"block",marginTop:8,color:"#047857"}}>These are seller-declared policy settings. Final eligibility and enforcement must also be handled by the PITICS order/returns backend.</small>
            </section>
            <section style={{padding:16,borderRadius:16,background:"#fff",border:"1px solid #c7d2fe",marginBottom:16}}>
              <div style={{fontWeight:900,color:"#312e81",marginBottom:4}}>🖼 Product gallery images</div>
              <small style={{color:"#64748b"}}>Existing images stay attached unless removed. Add more JPG/PNG/WebP images (up to 12 total).</small>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(115px,1fr))",gap:10,marginTop:12}}>
                {(editingProduct.galleryImages || []).map((img,i)=><div key={`${img.path}-${i}`} style={{position:"relative",padding:6,border:"1px solid #e2e8f0",borderRadius:12,background:"#f8fafc"}}>
                  <img src={img.url} alt={`Product ${i+1}`} style={{width:"100%",height:92,objectFit:"cover",borderRadius:8}}/>
                  <select aria-label="Image orientation" value={img.orientation || "square"} onChange={e=>setEditingProduct(p=>({...p,galleryImages:p.galleryImages.map((x,j)=>j===i?{...x,orientation:e.target.value}:x)}))} style={{width:"100%",marginTop:5,padding:5,borderRadius:7,border:"1px solid #cbd5e1"}}>
                    <option value="landscape">Landscape</option><option value="portrait">Portrait</option><option value="square">Square</option>
                  </select>
                  <button type="button" onClick={()=>setEditingProduct(p=>({...p,galleryImages:p.galleryImages.filter((_,j)=>j!==i),removeImagePaths:[...(p.removeImagePaths||[]),...(img.path?[img.path]:[])]}))} style={{width:"100%",marginTop:5,border:0,borderRadius:7,padding:6,background:"#fee2e2",color:"#b91c1c",fontWeight:800,cursor:"pointer"}}>Remove</button>
                </div>)}
                {(editingProduct.newGalleryFiles || []).map((img,i)=><div key={`new-${i}`} style={{position:"relative",padding:6,border:"1px solid #a5f3fc",borderRadius:12,background:"#ecfeff"}}>
                  <img src={img.previewUrl} alt={`New product ${i+1}`} style={{width:"100%",height:92,objectFit:"cover",borderRadius:8}}/>
                  <select value={img.orientation || "square"} onChange={e=>setEditingProduct(p=>({...p,newGalleryFiles:p.newGalleryFiles.map((x,j)=>j===i?{...x,orientation:e.target.value}:x)}))} style={{width:"100%",marginTop:5,padding:5,borderRadius:7,border:"1px solid #cbd5e1"}}><option value="landscape">Landscape</option><option value="portrait">Portrait</option><option value="square">Square</option></select>
                  <button type="button" onClick={()=>setEditingProduct(p=>({...p,newGalleryFiles:p.newGalleryFiles.filter((_,j)=>j!==i)}))} style={{width:"100%",marginTop:5,border:0,borderRadius:7,padding:6,background:"#fee2e2",color:"#b91c1c",fontWeight:800,cursor:"pointer"}}>Remove</button>
                </div>)}
              </div>
              <label style={{display:"inline-flex",alignItems:"center",gap:8,marginTop:12,padding:"10px 14px",borderRadius:10,background:"#eef2ff",color:"#4338ca",fontWeight:900,cursor:"pointer"}}>
                ＋ Add images
                <input type="file" hidden multiple accept="image/jpeg,image/png,image/webp" onChange={e=>{
                  const files=Array.from(e.target.files||[]);
                  const current=(editingProduct.galleryImages||[]).length+(editingProduct.newGalleryFiles||[]).length;
                  const accepted=files.filter(f=>/^image\/(jpeg|png|webp)$/.test(f.type)&&f.size<=5*1024*1024);
                  if(accepted.length!==files.length) alert("Only JPG, PNG or WebP images up to 5 MB each are accepted.");
                  const room=Math.max(0,12-current);
                  const added=accepted.slice(0,room).map(file=>({file,previewUrl:URL.createObjectURL(file),orientation:"square"}));
                  if(accepted.length>room) alert("You can keep a maximum of 12 product images.");
                  setEditingProduct(p=>({...p,newGalleryFiles:[...(p.newGalleryFiles||[]),...added]}));
                  e.target.value="";
                }}/>
              </label>
              <div style={{fontSize:12,color:"#64748b",marginTop:7}}>{(editingProduct.galleryImages||[]).length+(editingProduct.newGalleryFiles||[]).length} / 12 images retained or added</div>
            </section>

            <div style={{ padding: 12, border: "1px solid #c7d2fe", borderRadius: 10, marginBottom: 12 }}>
              <strong style={{ display: "block", marginBottom: 6 }}>Replace product preview image (optional)</strong>
              <small style={{ display: "block", color: "#64748b", marginBottom: 8 }}>Recommended 1200 × 800 px (3:2 landscape), JPG/PNG/WebP, under 500 KB; maximum 5 MB.</small>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) { alert("Preview image must be 5 MB or smaller."); e.target.value = ""; return; }
                setEditingProduct({ ...editingProduct, newPreviewImage: file });
                e.target.value = "";
              }} style={inputStyle} />
              {editingProduct.newPreviewImage && <small>{editingProduct.newPreviewImage.name}</small>}
              {!editingProduct.newPreviewImage && !editingProduct.removePreviewImage && (editingProduct.productPreviewImagePath || editingProduct.previewImagePath) && <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",marginTop:8}}><img src={resolveSellerImageUrl(editingProduct.productPreviewImagePath || editingProduct.previewImagePath)} alt="Current product preview" style={{display:"block",width:160,height:100,objectFit:"cover",borderRadius:10}}/><button type="button" onClick={()=>setEditingProduct(p=>({...p,removePreviewImage:true}))} style={{border:0,borderRadius:9,padding:"8px 12px",background:"#fee2e2",color:"#b91c1c",fontWeight:800,cursor:"pointer"}}>Remove preview</button></div>}
              {editingProduct.removePreviewImage && <small style={{color:"#b91c1c",fontWeight:700}}>Current preview will be removed when the server supports this update flag.</small>}
            </div>

            {editingProduct.newImage && (
              <select
                value={editingProduct.newImageOrientation || "square"}
                onChange={(e) => setEditingProduct({ ...editingProduct, newImageOrientation: e.target.value })}
                style={inputStyle}
              >
                <option value="landscape">Landscape</option>
                <option value="square">Square</option>
                <option value="portrait">Portrait</option>
              </select>
            )}

            {youtubeShortsEnabled && (
              <input
                value={editingProduct.youtubeShortUrl || ""}
                onChange={(e) => setEditingProduct({ ...editingProduct, youtubeShortUrl: e.target.value })}
                placeholder="YouTube Short URL: https://www.youtube.com/shorts/VIDEO_ID"
                style={inputStyle}
              />
            )}

            <section style={{ margin: "14px 0 18px", padding: 16, borderRadius: 16, border: "1px solid #bfdbfe", background: "#eff6ff" }}>
              <div style={{ fontWeight: 900, fontSize: 16, color: "#1e3a8a" }}>🎁 PITICS Share &amp; Earn — Free Gift</div>
              <p style={{ margin: "6px 0 12px", color: "#475569", fontSize: 13, lineHeight: 1.5 }}>
                Enter the separate gift item you will provide. Share &amp; Earn eligibility, campaign availability and sharing limits remain controlled by PITICS Admin.
              </p>
              <label style={{ display: "flex", gap: 10, alignItems: "center", fontWeight: 800, marginBottom: 12 }}>
                <input type="checkbox" checked={Boolean(editingProduct.shareGiftEnabled)} onChange={(e) => setEditingProduct({ ...editingProduct, shareGiftEnabled: e.target.checked })} />
                Participate in Share &amp; Earn and provide this free gift
              </label>
              {editingProduct.shareGiftEnabled && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
                  <label style={{ gridColumn: "1 / -1", fontWeight: 700, fontSize: 13 }}>Free gift product name *
                    <input required value={editingProduct.shareGiftProductName || ""} onChange={e => setEditingProduct({ ...editingProduct, shareGiftProductName: e.target.value })} placeholder="Gift product name" style={{ ...inputStyle, marginTop: 5 }} />
                  </label>
                  <label style={{ fontWeight: 700, fontSize: 13 }}>Gift value / price (₹) *
                    <input required type="number" min="0.01" step="0.01" value={editingProduct.shareGiftProductPrice ?? ""} onChange={e => setEditingProduct({ ...editingProduct, shareGiftProductPrice: e.target.value })} style={{ ...inputStyle, marginTop: 5 }} />
                  </label>
                  <label style={{ fontWeight: 700, fontSize: 13 }}>Available gift quantity *
                    <input required type="number" min="1" step="1" value={editingProduct.shareGiftProductQty ?? ""} onChange={e => setEditingProduct({ ...editingProduct, shareGiftProductQty: e.target.value })} style={{ ...inputStyle, marginTop: 5 }} />
                  </label>
                  <label style={{ gridColumn: "1 / -1", fontWeight: 700, fontSize: 13 }}>Gift product preview image
                    <input type="file" accept="image/jpeg,image/png,image/webp" required={!editingProduct.shareGiftImagePath} onChange={e => setEditingProduct({ ...editingProduct, shareGiftImage: e.target.files?.[0] || null })} style={{ ...inputStyle, marginTop: 5 }} />
                    <small style={{ display: "block", color: "#64748b", fontWeight: 400 }}>{editingProduct.shareGiftImage?.name || (editingProduct.shareGiftImagePath ? "Existing gift image saved — upload only to replace it." : "Upload a JPG, PNG or WEBP image.")}</small>
                    {(editingProduct.shareGiftImage instanceof File || editingProduct.shareGiftImageUrl) && <img src={editingProduct.shareGiftImage instanceof File ? URL.createObjectURL(editingProduct.shareGiftImage) : editingProduct.shareGiftImageUrl} alt="Free gift preview" style={{ width: 120, height: 100, objectFit: "contain", background: "#fff", borderRadius: 10, marginTop: 8 }} />}
                  </label>
                </div>
              )}
            </section>

            <div style={{
              marginTop: 22, padding: 18, background: "#f8fafc",
              borderRadius: 20, border: "1px solid #e2e8f0"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontWeight: 900, fontSize: 17 }}>Product Options & Variants</div>
                  <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
                    Select Size or Color directly, or add Material, Storage, Style and custom options.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {Object.keys(QUICK_OPTION_PRESETS).map(name => (
                    <button key={name} type="button"
                      onClick={() => addOption(setEditingProduct, name)}
                      disabled={(editingProduct.productOptions || []).length >= 5 || (editingProduct.productOptions || []).some(o => String(o?.name || "").trim().toLowerCase() === name.toLowerCase())}
                      style={{ border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", padding: "8px 11px", borderRadius: 11, fontWeight: 800, cursor: "pointer" }}>
                      ＋ {name}
                    </button>
                  ))}
                  <button type="button" onClick={() => addCustomOption(setEditingProduct)}
                    disabled={(editingProduct.productOptions || []).length >= 5}
                    style={{ border: "none", background: "#0f172a", color: "#fff", padding: "9px 13px", borderRadius: 12, fontWeight: 800, cursor: "pointer" }}>
                    ＋ Custom Option
                  </button>
                </div>
              </div>

              {(editingProduct.productOptions || []).map((option, oi) => (
                <div key={`edit-option-${oi}`} style={{ marginTop: 14, padding: 14, background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", gap: 10 }}>
                    <input value={option.name}
                      onChange={e => updateOption(setEditingProduct, oi, e.target.value)}
                      placeholder="Option name e.g. Color / Size"
                      style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                    <button type="button" onClick={() => removeOption(setEditingProduct, oi)}
                      style={{ border: "none", background: "#fee2e2", color: "#b91c1c", padding: "10px 12px", borderRadius: 12, fontWeight: 900, cursor: "pointer" }}>
                      Remove
                    </button>
                  </div>

                  {(option.values || []).map((value, vi) => (
                    <div key={`edit-value-${oi}-${vi}`} style={{ display: "flex", gap: 8, marginTop: 9 }}>
                      <input value={value}
                        onChange={e => updateOptionValue(setEditingProduct, oi, vi, e.target.value)}
                        placeholder={`Value ${vi + 1}`}
                        style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                      <button type="button" onClick={() => removeOptionValue(setEditingProduct, oi, vi)}
                        style={{ border: "none", background: "#f1f5f9", padding: "8px 11px", borderRadius: 10, cursor: "pointer" }}>
                        ✕
                      </button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addOptionValue(setEditingProduct, oi)}
                    style={{ marginTop: 10, border: "none", background: "#e0f2fe", color: "#0369a1", padding: "8px 11px", borderRadius: 10, fontWeight: 800, cursor: "pointer" }}>
                    ＋ Value
                  </button>
                </div>
              ))}

              {(editingProduct.productOptions || []).length > 0 && (
                <button type="button" onClick={() => generateRows(editingProduct, setEditingProduct)}
                  style={{ marginTop: 14, width: "100%", border: "none", background: "#2563eb", color: "#fff", padding: "12px 16px", borderRadius: 13, fontWeight: 900, cursor: "pointer" }}>
                  Generate / Refresh Variants
                </button>
              )}

              {(editingProduct.variants || []).length > 0 && (
                <div style={{ marginTop: 14, overflowX: "auto" }}>
                  <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", fontSize: 13 }}>
                    <thead><tr>
                      {(editingProduct.productOptions || []).map(o => <th key={o.name} style={{ textAlign: "left", padding: 8 }}>{o.name}</th>)}
                      {["SKU", "MRP", "Price", "Stock", "Min", "Step"].map(x => <th key={x} style={{ textAlign: "left", padding: 8 }}>{x}</th>)}
                    </tr></thead>
                    <tbody>
                      {editingProduct.variants.map((v, vi) => (
                        <tr key={v.variantId || vi}>
                          {(editingProduct.productOptions || []).map(o =>
                            <td key={o.name} style={{ padding: 8, borderTop: "1px solid #e2e8f0", fontWeight: 700 }}>{v.options?.[o.name] || "-"}</td>
                          )}
                          {["sku", "mrp", "sellingPrice", "stockQty", "minOrderQty", "orderStep"].map(field => (
                            <td key={field} style={{ padding: 8, borderTop: "1px solid #e2e8f0" }}>
                              <input type={field === "sku" ? "text" : "number"}
                                min={field === "minOrderQty" || field === "orderStep" ? 1 : 0}
                                value={v[field] ?? ""}
                                onChange={e => updateVariantField(setEditingProduct, vi, field, e.target.value)}
                                style={{ width: field === "sku" ? 130 : 90, padding: "8px 9px", borderRadius: 9, border: "1px solid #cbd5e1" }} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 14,
                marginTop: 20
              }}
            >

              <button
                type="button"
                onClick={
                  updateProduct
                }
                disabled={saving}
                style={{
                  border: "none",
                  background:
                    "#2563eb",
                  color: "#fff",
                  padding:
                    "14px 18px",
                  borderRadius: 16,
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                {saving
                  ? "Saving..."
                  : "Save"}
              </button>

              <button
                type="button"
                onClick={() =>
                  setEditingProduct(
                    null
                  )
                }
                style={{
                  border: "none",
                  background:
                    "#e2e8f0",
                  padding:
                    "14px 18px",
                  borderRadius: 16,
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}